import { mergeAttributes, Node } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import { NodeViewWrapper, ReactNodeViewRenderer, ReactRenderer, type NodeViewProps } from '@tiptap/react'
import Suggestion from '@tiptap/suggestion'
import { AlertTriangle, Braces } from 'lucide-react'
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    type KeyboardEvent,
} from 'react'
import tippy, { type GetReferenceClientRect, type Instance } from 'tippy.js'

import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

import type { EmailVariable } from '../types'

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        emailVariable: {
            /** Insert a variable pill at the current selection */
            insertVariable: (variable: EmailVariable) => ReturnType
        }
    }
}

const PluginKeyInstance = new PluginKey('emailVariable')

export interface VariableOptions {
    /** List of variables shown in the `@` picker */
    variables: EmailVariable[]
}

/**
 * Custom variable extension inspired by Maily's variable node.
 *
 * Key differences vs Maily:
 *   - Typed variable contract (`EmailVariable`) sourced from tagada's templateRegistry
 *   - Renders a compact pill with icon + label + optional required warning
 *   - Serialises to `{{ key }}` / `{{ key,fallback=value }}` in HTML via renderer.ts
 *   - `shadcn/radix` popover instead of Tippy for the pill editor
 */
export const VariableExtension = Node.create<VariableOptions>({
    name: 'emailVariable',
    group: 'inline',
    inline: true,
    selectable: true,
    atom: true,

    addOptions() {
        return {
            variables: [],
        }
    },

    addAttributes() {
        return {
            key: {
                default: '',
                parseHTML: (el) => el.getAttribute('data-key') || '',
                renderHTML: (attrs) => ({ 'data-key': attrs.key }),
            },
            label: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-label'),
                renderHTML: (attrs) => (attrs.label ? { 'data-label': attrs.label } : {}),
            },
            fallback: {
                default: '',
                parseHTML: (el) => el.getAttribute('data-fallback') || '',
                renderHTML: (attrs) =>
                    attrs.fallback ? { 'data-fallback': attrs.fallback } : {},
            },
            required: {
                default: false,
                parseHTML: (el) => el.getAttribute('data-required') === 'true',
                renderHTML: (attrs) =>
                    attrs.required ? { 'data-required': 'true' } : {},
            },
        }
    },

    parseHTML() {
        return [{ tag: 'span[data-type="email-variable"]' }]
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, { 'data-type': 'email-variable' }),
        ]
    },

    addCommands() {
        return {
            insertVariable:
                (variable) =>
                ({ chain }) =>
                    chain()
                        .focus()
                        .insertContent({
                            type: this.name,
                            attrs: {
                                key: variable.key,
                                label: variable.label || variable.key,
                                required: variable.required || false,
                            },
                        })
                        .insertContent(' ')
                        .run(),
        }
    },

    addNodeView() {
        return ReactNodeViewRenderer(VariableNodeView)
    },

    addProseMirrorPlugins() {
        return [
            Suggestion<EmailVariable, EmailVariable>({
                editor: this.editor,
                pluginKey: PluginKeyInstance,
                char: '@',
                allowSpaces: false,
                startOfLine: false,
                command: ({ editor, range, props }) => {
                    editor
                        .chain()
                        .focus()
                        .deleteRange(range)
                        .insertVariable(props)
                        .run()
                },
                items: ({ query }) => {
                    const all = this.options.variables
                    if (!query) return all.slice(0, 12)
                    const q = query.toLowerCase()
                    return all
                        .filter((v) => {
                            const hay = `${v.key} ${v.label || ''} ${v.group || ''}`.toLowerCase()
                            return hay.includes(q)
                        })
                        .slice(0, 12)
                },
                render: () => {
                    let component: ReactRenderer<VariableListHandle> | null = null
                    let popup: Instance[] | null = null

                    return {
                        onStart: (props) => {
                            component = new ReactRenderer(VariableSuggestionList, {
                                props,
                                editor: props.editor,
                            })
                            if (!props.clientRect) return
                            popup = tippy('body', {
                                getReferenceClientRect:
                                    props.clientRect as GetReferenceClientRect,
                                appendTo: () => document.body,
                                content: component.element,
                                showOnCreate: true,
                                interactive: true,
                                trigger: 'manual',
                                placement: 'bottom-start',
                            })
                        },
                        onUpdate: (props) => {
                            component?.updateProps(props)
                            const popupInstance = popup?.[0]
                            if (!popupInstance || popupInstance.state.isDestroyed) return
                            if (!props.clientRect) return
                            popupInstance.setProps({
                                getReferenceClientRect:
                                    props.clientRect as GetReferenceClientRect,
                            })
                        },
                        onKeyDown: (props) => {
                            if (props.event.key === 'Escape') {
                                popup?.[0]?.destroy()
                                component?.destroy()
                                return true
                            }
                            return component?.ref?.onKeyDown(props.event) ?? false
                        },
                        onExit: () => {
                            popup?.[0]?.destroy()
                            component?.destroy()
                            popup = null
                            component = null
                        },
                    }
                },
            }),
        ]
    },
})

/* ----------------------------------------------------------------------------
   Pill node view — what appears inside the editor
---------------------------------------------------------------------------- */

function VariableNodeView(props: NodeViewProps) {
    const { node, updateAttributes, deleteNode } = props
    const { t } = useTranslation('emails')
    const { key, label, fallback, required } = node.attrs as {
        key: string
        label: string | null
        fallback: string
        required: boolean
    }

    const displayLabel = label || key || 'variable'
    const showWarning = required && !fallback
    const [open, setOpen] = useState(false)

    return (
        <NodeViewWrapper as="span" className="inline-block align-middle">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        contentEditable={false}
                        className={cn(
                            'mx-0.5 inline-flex max-w-[240px] items-center gap-1 rounded-md border px-1.5 py-px align-middle font-mono text-[0.8em] leading-tight transition-colors',
                            'border-rose-200 bg-rose-50/80 text-rose-700 hover:bg-rose-100',
                            showWarning &&
                                'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100',
                        )}
                    >
                        <Braces className="h-3 w-3 shrink-0 stroke-[2.5]" />
                        <span className="truncate">{displayLabel}</span>
                        {showWarning && (
                            <AlertTriangle className="h-3 w-3 shrink-0 stroke-[2.5]" />
                        )}
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    side="bottom"
                    sideOffset={6}
                    className="w-72 space-y-3 p-3"
                >
                    <div className="space-y-1">
                        <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {t('builder.variable.variableKey')}
                        </label>
                        <Input
                            value={key}
                            onChange={(e) =>
                                updateAttributes({ key: e.target.value })
                            }
                            placeholder={t('builder.variable.variableKeyPlaceholder')}
                            className="h-8 font-mono text-xs"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            {t('builder.variable.fallbackIfMissing')}
                        </label>
                        <Input
                            value={fallback}
                            onChange={(e) =>
                                updateAttributes({ fallback: e.target.value })
                            }
                            placeholder={t('builder.variable.fallbackPlaceholder')}
                            className="h-8 text-xs"
                        />
                        <p className="text-[11px] text-muted-foreground">
                            {t('builder.variable.fallbackHint')}
                        </p>
                    </div>
                    <div className="flex items-center justify-between border-t pt-2 text-xs">
                        <label className="flex items-center gap-2 text-muted-foreground">
                            <input
                                type="checkbox"
                                checked={required}
                                onChange={(e) =>
                                    updateAttributes({ required: e.target.checked })
                                }
                            />
                            {t('builder.variable.required')}
                        </label>
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false)
                                deleteNode()
                            }}
                            className="rounded-md px-2 py-1 text-rose-600 hover:bg-rose-50"
                        >
                            {t('builder.variable.remove')}
                        </button>
                    </div>
                </PopoverContent>
            </Popover>
        </NodeViewWrapper>
    )
}

/* ----------------------------------------------------------------------------
   Suggestion list shown on `@`
---------------------------------------------------------------------------- */

type VariableListHandle = { onKeyDown: (e: KeyboardEvent | globalThis.KeyboardEvent) => boolean }

interface SuggestionListProps {
    items: EmailVariable[]
    command: (variable: EmailVariable) => void
}

const VariableSuggestionList = forwardRef<VariableListHandle, SuggestionListProps>(
    function VariableSuggestionList({ items, command }, ref) {
        const { t } = useTranslation('emails')
        const [selected, setSelected] = useState(0)
        const listRef = useRef<HTMLDivElement>(null)

        useEffect(() => setSelected(0), [items])

        useEffect(() => {
            const el = listRef.current?.querySelector<HTMLElement>(
                `[data-index="${selected}"]`,
            )
            el?.scrollIntoView({ block: 'nearest' })
        }, [selected])

        useImperativeHandle(ref, () => ({
            onKeyDown: (event) => {
                if (event.key === 'ArrowUp') {
                    setSelected((i) => (items.length === 0 ? 0 : (i - 1 + items.length) % items.length))
                    return true
                }
                if (event.key === 'ArrowDown') {
                    setSelected((i) => (items.length === 0 ? 0 : (i + 1) % items.length))
                    return true
                }
                if (event.key === 'Enter' || event.key === 'Tab') {
                    const item = items[selected]
                    if (item) {
                        command(item)
                        return true
                    }
                }
                return false
            },
        }))

        const grouped = groupByGroup(items)

        if (items.length === 0) {
            return (
                <div className="w-64 rounded-lg border bg-popover p-3 text-sm text-muted-foreground shadow-md">
                    {t('builder.variable.noVariablesMatch')}
                </div>
            )
        }

        let flatIndex = -1

        return (
            <div
                ref={listRef}
                className="w-72 overflow-hidden rounded-lg border bg-popover text-sm shadow-md"
            >
                <div className="flex items-center justify-between border-b bg-muted/30 px-2 py-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <span>{t('builder.variable.variables')}</span>
                    <Braces className="h-3 w-3" />
                </div>
                <div className="max-h-64 overflow-y-auto p-1">
                    {Object.entries(grouped).map(([group, list]) => (
                        <div key={group} className="mb-1">
                            {group !== '__none__' && (
                                <div className="px-2 pb-0.5 pt-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
                                    {group}
                                </div>
                            )}
                            {list.map((v) => {
                                flatIndex += 1
                                const isSelected = flatIndex === selected
                                const capturedIndex = flatIndex
                                return (
                                    <button
                                        key={v.key}
                                        type="button"
                                        data-index={capturedIndex}
                                        onMouseEnter={() => setSelected(capturedIndex)}
                                        onClick={() => command(v)}
                                        className={cn(
                                            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left',
                                            isSelected && 'bg-muted',
                                        )}
                                    >
                                        <Braces className="h-3 w-3 shrink-0 text-rose-600" />
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate font-mono text-xs">
                                                {v.label || v.key}
                                            </div>
                                            {v.description && (
                                                <div className="truncate text-[11px] text-muted-foreground">
                                                    {v.description}
                                                </div>
                                            )}
                                        </div>
                                        {v.example && (
                                            <span className="truncate text-[10px] text-muted-foreground/70">
                                                {v.example}
                                            </span>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    ))}
                </div>
                <div className="flex items-center justify-between border-t bg-muted/20 px-2 py-1 text-[10px] text-muted-foreground">
                    <span>
                        <kbd className="rounded border bg-background px-1">↑↓</kbd>{' '}
                        {t('builder.variable.navigate')}
                    </span>
                    <span>
                        <kbd className="rounded border bg-background px-1">Enter</kbd>{' '}
                        {t('builder.variable.insert')}
                    </span>
                </div>
            </div>
        )
    },
)

function groupByGroup(vars: EmailVariable[]): Record<string, EmailVariable[]> {
    const acc: Record<string, EmailVariable[]> = {}
    for (const v of vars) {
        const g = v.group || '__none__'
        if (!acc[g]) acc[g] = []
        acc[g].push(v)
    }
    return acc
}
